import React from "react";
import { SurveyStoreType, SurveyType } from "modules/main/models/surveys-store";
import { observer } from "mobx-react";
import styles from "./Survey.module.scss";
import { ProgressBar, Checkbox, Icon } from "@blueprintjs/core";
import { Constants } from "modules/root/models/constants";
import { RemoveConfirmation } from "modules/common/components/form/RemoveConfirmation";

/**
 * Опрос берётся из стора по номеру, а не приходит пропом.
 *
 * Пропом сюда раньше передавался сам узел MST, и при каждой загрузке списка он
 * оказывался новым: `id` у наших моделей — обычная строка, а не
 * `types.identifier`, поэтому `applySnapshot` не может сопоставить прежние
 * элементы с новыми и пересоздаёт их.
 *
 * В dev-сборке React 19 сменившийся проп-объект разбирается по свойствам для
 * вкладки «Components» профайлера. Обход узла MST поднимает его ленивые поля вне
 * фазы инициализации, MST бросает проверку прямо в фазе коммита, и после этого
 * отложенные обновления перестают применяться — приложение перестаёт
 * перерисовываться при переходах по меню.
 *
 * Стор — тоже узел MST, но его тождество неизменно, поэтому React внутрь не
 * заходит. Реактивность не страдает: компонент остаётся observer и читает
 * поля опроса как раньше.
 */
export const Survey = observer(({ store, index, children, button }: SurveyProps) => {
  const survey = store.items[index];

  if (!survey) {
    return null;
  }

  const statusClassName = Constants.surveyInactiveStatuses.includes(survey.status) ? styles.inactive : styles.active;
  const canVote = !survey.voted && survey.active;

  return (
    <div className={survey.status === "Активен" ? styles.survey : styles.surveyEnded}>
      <div className={`planr-block-header flat-mode ${styles.header}`}>
        {survey.created}
        <span className={`${styles.status} ${statusClassName}`}>{survey.status}</span>
      </div>
      <div className={styles.surveyTop}>
        {survey.active && (
          <RemoveConfirmation<SurveyType>
            what={() => "опрос"}
            actionName="завершить"
            render={({ confirmRemoving }) => (
              <>
                <Icon
                  icon="cross"
                  className={styles.surveyEndIcon}
                  htmlTitle="Завершить"
                  onClick={() => confirmRemoving(survey)}
                />
                &nbsp;&nbsp;
              </>
            )}
            onConfirmed={() => survey.cancel()}
          />
        )}
        <span className={styles.surveyName}>{survey.name}</span>
      </div>
      <div className={survey.status === "Активен" ? styles.body : styles.bodyEnded}>
        {!canVote && (
          <div className={styles.result}>
            {Array.from(survey.results.entries()).map(([variant, percents]) => {
              return (
                <div key={variant} className={styles.stripe}>
                  {variant} - {percents}%
                  <div className={styles.progress}>
                    <ProgressBar intent="primary" value={percents / 100} animate={false} stripes={false} />
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {canVote && (
          <div className={styles.form}>
            {survey.variants.map((variant) => {
              const handler = () => survey.vote(variant);

              return (
                <div key={variant} className={styles.variant}>
                  <Checkbox large={false} checked={false} onChange={handler} label={variant} />
                </div>
              );
            })}
          </div>
        )}
      </div>
      <div className={styles.footer}>
        {button}
        Проголосовало
        <span className={styles.peopleCount}>{survey.votes.size} человек</span>
        {children}
      </div>
    </div>
  );
});

interface SurveyProps {
  store: SurveyStoreType;
  index: number;
  children?: any;
  button?: any;
}
